'use client'

import { useCallback, useEffect, useState } from "react";
import { useCookie } from "@/hooks/useCookie";
import { useHost } from "@/hooks/useHost";
import useWebSocket from "@/hooks/useWebSocket";
import { useRouter } from "next/navigation";
import { QRCodeCanvas } from "qrcode.react";
import { cn } from "@/lib/utils";
import { useUser } from "@/stores/appStore";
import toast from "react-hot-toast";
import { AlertCircleIcon, CheckCircleIcon, ResetIcon } from "../fun-ui/icons";
import { buildQrCodeStatusWsUrl } from "@/lib/websocket/urls";

interface WeChatQRCodeLoginProps extends React.HTMLAttributes<HTMLDivElement> {
}
export function WeChatQRCodeLogin({
  className = '',
}: WeChatQRCodeLoginProps) {
  const router = useRouter();
  const { host } = useHost();
  const SESSID = useCookie('FDSID');

  const {
    refreshUserInfo,
  } = useUser();

  const [scanned, setScanned] = useState(false);
  const [canceled, setCanceled] = useState(false);
  useEffect(() => {
    console.log('SESSID', SESSID);
  }, [SESSID])

  // WebSocket 消息处理
  const handleWebSocketMessage = useCallback((event: MessageEvent) => {
    (async () => {
      try {
        const data = JSON.parse(event.data);
        console.log('【QR WebSocket】收到消息:', data);

        if (data.event === 'confirmed') {
          const user = await refreshUserInfo();
          if (user?.user?.mobile) {
            // 用户已确认登录
            router.push('/papers');
          } else {
            toast.error('扫码失败，请重试')
          }
        } else if (data.event === 'scanned') {
          // 用户已扫码，但还未确认
          console.log('用户已扫码，等待确认...');
          setCanceled(false);
          setScanned(true);
        } else if (data.event === 'canceled') {
          setCanceled(true);
          setScanned(false);
          console.log('用户取消扫码');
        } else if (data.event === 'error') {
          // 发生错误
          console.error('二维码登录错误:', data.message);
        }
      } catch (error) {
        console.error('【QR WebSocket】消息解析失败:', error, event.data);
      }
    })();
  }, [refreshUserInfo, router]);

  // 使用 WebSocket 监听二维码状态
  const url = buildQrCodeStatusWsUrl(SESSID);
  useWebSocket({
    url,
    onMessage: handleWebSocketMessage,
    onOpen: () => console.log('【QR WebSocket】连接已建立'),
    onClose: () => console.log('【QR WebSocket】连接已关闭'),
    onError: (error) => {
      console.error('【QR WebSocket】连接错误:', {
        url,
        sid: SESSID,
        message: '二维码 WebSocket 连接失败，可能的原因：网络问题、服务器不可达、会话 ID 无效或 SSL/TLS 证书问题',
        originalEvent: error,
        timestamp: new Date().toISOString(),
      });
    },
  });

  // 组件卸载时确保清理
  useEffect(() => {
    return () => {
      console.log('【QR WebSocket】组件卸载，清理连接');
    };
  }, []);


  // value={host(`/qrcode?ssid=${SESSID}`)}
  return (
    <div className={cn(
      "flex flex-col items-center justify-center w-[17.5rem] h-[16.875rem] relative gap-2",
      className
    )}>
      <div className="relative flex items-center justify-center border border-border border-solid p-4">
        <QRCodeCanvas
          value={host(`/qrcode?ssid=${SESSID}`)}
          size={200}
          level="M"
        />
        {
          scanned ? (
            <div
              className="absolute inset-0 bg-background/90 flex flex-col items-center justify-center gap-2 backdrop-blur-sm"
            >
              <CheckCircleIcon size={40} className="text-green-500" />
              <div className="flex items-center">
                扫码成功
              </div>
              <div className="flex items-center text-muted-foreground text-sm">
                请在手机端确认登录
              </div>
              <div
                className="flex items-center text-muted-foreground text-sm gap-1 cursor-pointer"
                onClick={() => {
                  setScanned(false);
                }}
              >
                <ResetIcon size={12} />
                <span>刷新二维码</span>
              </div>
            </div>
          ) : null
        }
      </div>
      {
        canceled ? (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <AlertCircleIcon size={12} className="text-orange-600" />
            <span>手机端取消登录，重新扫码</span>
          </div>
        ) : null
      }
      <div className="flex items-center justify-center">
        使用微信扫一扫登录
      </div>
    </div>
  );
}
